import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useLang } from '../i18n/context';
import {
  getSocialCutoff,
  groupPostsByAccount,
  normalizeSocialPost,
} from './socialData';
import './SocialPage.css';
import '../styles/pro-page.css';

function SocialPage() {
  const navigate = useNavigate();
  const { username } = useParams();
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [accountRows, setAccountRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchSocial = async () => {
      setLoading(true);
      try {
        const cutoff = getSocialCutoff();
        const [postsResult, accountsResult] = await Promise.all([
          supabase
            .from('social_posts')
            .select('*')
            .gte('posted_at', cutoff)
            .order('posted_at', { ascending: false }),
          supabase
            .from('social_accounts')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true }),
        ]);

        if (postsResult.error) throw postsResult.error;
        if (!cancelled) {
          setPosts(postsResult.data || []);
          setAccountRows(accountsResult.error ? [] : (accountsResult.data || []));
        }
      } catch {
        if (!cancelled) {
          setPosts([]);
          setAccountRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSocial();
    return () => { cancelled = true; };
  }, []);

  const accounts = useMemo(() => groupPostsByAccount(posts, accountRows), [posts, accountRows]);
  const activeUsername = String(username || '').replace(/^@/, '').toLowerCase();
  const activeAccount = accounts.find((account) => account.username === activeUsername);
  const isTimeline = Boolean(username);

  const formatDate = (date, includeTime = false) => {
    if (!date) return '';
    return new Date(date).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  };

  const copy = {
    accountsEyebrow: lang === 'zh' ? '关注账号' : 'Following',
    accountsTitle: lang === 'zh' ? '社区消息' : 'Community Feed',
    accountsDesc: lang === 'zh' ? '按账号查看来自 X 的最新观点，只展示最近 15 天的消息。' : 'Browse recent insights from X by account. Only the latest 15 days are shown.',
    recentPosts: lang === 'zh' ? '近 15 天动态' : 'Posts in 15 days',
    latestUpdate: lang === 'zh' ? '最近更新' : 'Latest update',
    backAccounts: lang === 'zh' ? '返回账号列表' : 'Back to accounts',
    noAccount: lang === 'zh' ? '这个账号最近 15 天暂无动态' : 'No posts from this account in the last 15 days',
    timeline: lang === 'zh' ? '时间线' : 'Timeline',
  };

  return (
    <main className="pro-page">
      <div className="pro-page-inner">
        <button type="button" className="pro-back" onClick={() => navigate(isTimeline ? '/social' : '/')}>
          {isTimeline ? copy.backAccounts : t('airdrop.backHome')}
        </button>
        <header className="pro-hero">

          {isTimeline && activeAccount ? (
            <AccountHeader account={activeAccount} postLabel={copy.recentPosts} />
          ) : (
            <>
              <p className="pro-hero-label">{copy.accountsEyebrow}</p>
              <h1>{copy.accountsTitle}</h1>
              <span className="pro-hero-desc">{copy.accountsDesc}</span>
            </>
          )}
        </header>

      <section className="sp-content">
        {loading ? (
          <div className="sp-empty">{t('social.loading')}</div>
        ) : isTimeline ? (
          activeAccount ? (
            <div className="sp-timeline">
              <div className="sp-section-title">
                <h2>{copy.timeline}</h2>
                <span>{activeAccount.post_count} {copy.recentPosts}</span>
              </div>
              {activeAccount.posts.map((post) => (
                <PostCard key={post.id} post={post} formatDate={formatDate} t={t} />
              ))}
            </div>
          ) : (
            <div className="sp-empty">{copy.noAccount}</div>
          )
        ) : accounts.length === 0 ? (
          <div className="sp-empty">{t('social.empty')}</div>
        ) : (
          <div className="sp-account-grid">
            {accounts.map((account) => (
              <button
                type="button"
                className="sp-account-card"
                key={account.username}
                onClick={() => navigate(`/social/${account.username}`)}
              >
                <Avatar account={account} />
                <span className="sp-account-card-main">
                  <strong>{account.display_name}</strong>
                  <em>@{account.username}</em>
                  {account.bio && <span className="sp-account-bio">{account.bio}</span>}
                  <span className="sp-account-latest">
                    {account.latest_post?.content || ''}
                  </span>
                </span>
                <span className="sp-account-card-meta">
                  <strong>{account.post_count}</strong>
                  <span>{copy.recentPosts}</span>
                  <time>{copy.latestUpdate}: {formatDate(account.latest_post?.posted_at)}</time>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
      </div>
    </main>
  );
}

function AccountHeader({ account, postLabel }) {
  return (
    <div className="sp-account-header">
      <Avatar account={account} large />
      <div>
        <p className="sp-eyebrow">X / Twitter</p>
        <h1>{account.display_name}</h1>
        <a href={account.profile_url} target="_blank" rel="noopener noreferrer">@{account.username}</a>
        {account.bio && <p>{account.bio}</p>}
      </div>
      <div className="sp-account-stat">
        <strong>{account.post_count}</strong>
        <span>{postLabel}</span>
      </div>
    </div>
  );
}

function Avatar({ account, large = false }) {
  const initial = (account.display_name || account.username || '?').slice(0, 1).toUpperCase();
  return account.avatar_url ? (
    <img className={`sp-avatar${large ? ' sp-avatar-large' : ''}`} src={account.avatar_url} alt="" />
  ) : (
    <span className={`sp-avatar sp-avatar-fallback${large ? ' sp-avatar-large' : ''}`}>{initial}</span>
  );
}

function PostCard({ post: rawPost, formatDate, t }) {
  const post = normalizeSocialPost(rawPost);
  const metricItems = [
    ['likes', post.metrics.likes],
    ['reposts', post.metrics.reposts],
    ['replies', post.metrics.replies],
    ['views', post.metrics.views],
  ].filter(([, value]) => value !== null && value !== undefined);

  return (
    <article className="sp-post">
      <div className="sp-post-body">
        <div className="sp-post-author">
          <Avatar account={{ display_name: post.author_name, username: post.author_username, avatar_url: post.author_avatar }} />
          <div>
            <strong>{post.author_name}</strong>
            <span>@{post.author_username}</span>
          </div>
          <time>{formatDate(post.posted_at, true)}</time>
        </div>
        <p className="sp-post-text">{post.content}</p>
        {post.images.length > 0 && (
          <div className={`sp-post-images sp-post-images-${Math.min(post.images.length, 4)}`}>
            {post.images.slice(0, 4).map((src, index) => (
              <img key={src || index} src={src} alt={t('social.imageAlt')} loading="lazy" />
            ))}
          </div>
        )}
      </div>
      <footer className="sp-post-footer">
        <div className="sp-post-metrics">
          {metricItems.map(([label, value]) => <span key={label}>{formatMetric(value)} {label}</span>)}
        </div>
        {post.source_url && (
          <a href={post.source_url} target="_blank" rel="noopener noreferrer">
            {t('social.viewSource')} →
          </a>
        )}
      </footer>
    </article>
  );
}

function formatMetric(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(number);
}

export default SocialPage;
